import type Fuse from "fuse.js";
import { curry, defaultTo, pipe } from "ramda";

import cx from "@hsl/core/utils/cx";

import { Translate } from "../../hooks/useTranslation";
import HighlightedText from "./HighlightedText";
import useFundFinderStore from "./store";
import { type DataWithType, type SearchResults } from "./types";

type Props = {
    results?: SearchResults;
    getter: (id: string) => DataWithType | undefined;
    absoluteURL: (rel: string, root?: string) => string;
    footer: React.ReactNode | undefined;
    containerWidth?: number;
};
export default function SearchResults({
    results,
    getter: getDataByIdWithType,
    absoluteURL,
    footer,
    containerWidth,
}: Props) {
    const debug = useFundFinderStore((store) =>
        store.search.term.startsWith("hedged to gbp"),
    );
    const fundData = curry((props: string | string[], id: string) => {
        const obj = getDataByIdWithType(id);
        if (!obj) {
            return "";
        }
        const prop = Array.isArray(props)
            ? props.find((p) => p in obj.data.data)
            : props;
        return prop ? obj.data.data[prop] : "";
    });

    const fundName = pipe(fundData(["name", "portfolio_name"]), defaultTo("-"));
    const fundType = pipe(fundData("productType"), defaultTo("-"));
    const fundBenchmark = pipe(fundData("benchmark"), defaultTo("-"));
    const capabilityStyle = pipe(
        fundData(["capabilityStyle", "assetClassStyle"]),
        defaultTo("-"),
    );
    const fundPageURL = (id: string) => {
        const fundObj = getDataByIdWithType(id);
        if (!fundObj) {
            return "";
        }
        const fundURL = fundObj.data.data["fundPageURL"] as string | undefined;
        return fundURL && fundURL.length > 10
            ? absoluteURL(fundURL)
            : `${fundObj.url}?search=${encodeURIComponent(
                  (fundObj.data.data["name"] as string) ?? "",
              )}`;
    };
    const getMatch = curry(
        (props: string | string[], matches: Fuse.FuseResultMatch[]) => {
            const prop = Array.isArray(props)
                ? props.find((p) => matches.some((m) => m.key === p))
                : props;
            const _matches = matches.filter(
                (m) => m.key === prop && Boolean(m.value),
            );
            return _matches && _matches.length
                ? _matches.reduce((acc, match) => {
                      if (!acc.value || acc.value === match.value) {
                          return {
                              ...acc,
                              indices: [...acc.indices, ...match.indices],
                          };
                      }
                      return acc;
                  })
                : undefined;
        },
    );

    const nameMatch = getMatch(["name", "portfolio_name"]);
    const tickerMatch = getMatch("bloomberg");
    const benchmarkMatch = getMatch("benchmark");
    const productTypeMatch = getMatch("productType");
    const scopeCodeMatch = getMatch("scopeCode");
    const isinMatch = getMatch("isin");
    const capabilityMatch = getMatch(["capabilityStyle", "assetClassStyle"]);

    const fundDescription = (
        fundId: string,
        matches: Fuse.FuseResultMatch[],
    ) => {
        const benchmark = benchmarkMatch(matches);
        const fbenchmark = fundBenchmark(fundId) as string;
        const isin = isinMatch(matches);
        const scopeCode = scopeCodeMatch(matches);
        const ticker = tickerMatch(matches);
        return scopeCode ? (
            <span>
                <span className="@md/main:text-0 text-taupe-grey @md/main:text-black">
                    <Translate>Fund code</Translate>:{" "}
                </span>
                <HighlightedText
                    indices={scopeCode.indices as [number, number][]}
                    text={scopeCode.value ?? ""}
                />
            </span>
        ) : isin ? (
            <span>
                <span className="@md/main:text-0 text-taupe-grey @md/main:text-black">
                    ISIN:{" "}
                </span>
                <HighlightedText
                    indices={isin.indices as [number, number][]}
                    text={isin.value ?? ""}
                />
            </span>
        ) : ticker ? (
            <span>
                <span className="@md/main:text-0 text-taupe-grey @md/main:text-black">
                    Ticker:{" "}
                </span>
                <HighlightedText
                    indices={ticker.indices as [number, number][]}
                    text={ticker.value ?? ""}
                />
            </span>
        ) : fbenchmark !== "-" ? (
            <span>
                <span className="@md/main:text-0 text-taupe-grey @md/main:text-black">
                    <Translate>Benchmark</Translate>:{" "}
                </span>
                {benchmark ? (
                    <HighlightedText
                        indices={benchmark?.indices as [number, number][]}
                        text={benchmark?.value ?? ""}
                    />
                ) : (
                    fbenchmark
                )}
            </span>
        ) : (
            ""
        );
    };
    const handleRowClick = (id: string) => () => {
        const fURL = fundPageURL(id);
        console.log(fURL);
        window.open(fURL);
    };
    return (
        <div className="relative z-[9999] w-full">
            <div className="absolute bg-white left-0 right-0 top-0 px-3.5 shadow-lg">
                {results ? (
                    <>
                        <div className="@md/main:grid sticky top-0 hidden grid-cols-[3fr_1fr_1fr] gap-3 bg-white py-3 font-semibold">
                            <div>
                                <Translate>Fund name</Translate>
                            </div>
                            <div>
                                <Translate>Fund type</Translate>
                            </div>
                            <div>
                                <Translate>Capability/Style</Translate>
                            </div>
                        </div>
                        <div className="max-h-[30rem] overflow-y-auto">
                            <div className={cx("flex w-full flex-col")}>
                                {Object.entries(results).map(
                                    ([fundId, matches]) => (
                                        <div
                                            key={fundId}
                                            onClick={handleRowClick(fundId)}
                                            className="@md/main:grid @md/main:grid-cols-[3fr_1fr_1fr] @md/main:gap-3 flex cursor-pointer flex-col gap-0 py-2 hover:bg-[#F6F6F6]"
                                        >
                                            <div className="leading-none">
                                                <span className="text-base font-medium leading-tight">
                                                    <HighlightedText
                                                        text={fundName(fundId)}
                                                        indices={
                                                            nameMatch(matches)
                                                                ?.indices as [
                                                                number,
                                                                number,
                                                            ][]
                                                        }
                                                    />
                                                </span>
                                                <br />
                                                <span className="text-sm leading-tight">
                                                    {fundDescription(
                                                        fundId,
                                                        matches,
                                                    )}
                                                </span>
                                            </div>
                                            <div className="@md/main:text-base text-sm">
                                                <span className="@md/main:hidden @md/main:text-0 text-taupe-grey">
                                                    <Translate>
                                                        Fund type
                                                    </Translate>
                                                    :{" "}
                                                </span>
                                                <HighlightedText
                                                    text={fundType(fundId)}
                                                    indices={
                                                        productTypeMatch(
                                                            matches,
                                                        )?.indices as [
                                                            number,
                                                            number,
                                                        ][]
                                                    }
                                                />
                                            </div>
                                            <div className="@md/main:text-base text-sm">
                                                <span className="@md/main:hidden @md/main:text-0 text-taupe-grey">
                                                    <Translate>
                                                        Capability/Style
                                                    </Translate>
                                                    :{" "}
                                                </span>
                                                <HighlightedText
                                                    text={
                                                        capabilityStyle(
                                                            fundId,
                                                        ) as unknown as string
                                                    }
                                                    indices={
                                                        capabilityMatch(matches)
                                                            ?.indices as [
                                                            number,
                                                            number,
                                                        ][]
                                                    }
                                                />
                                            </div>
                                        </div>
                                    ),
                                )}
                            </div>
                        </div>
                    </>
                ) : (
                    <div className="flex min-h-[100px] flex-col items-center justify-center">
                        <h3 className="font-sans text-lg font-medium">
                            No funds match your search
                        </h3>
                        <p className="font-sans text-base">
                            Please adjust your search criteria
                        </p>
                    </div>
                )}
                {footer && (
                    <div className="sticky bottom-0 border-t bg-white">
                        {footer}
                    </div>
                )}
            </div>
        </div>
    );
}
